/**
 * Phase 15 Plan 15-02 — MachineMonthlyEntriesPage
 *
 * Route: /logsheets/monthly-summary/:machineId?month=YYYY-MM
 * Permission gate: logsheet.monthly.read
 *
 * Drill-through view of all entries for a (machine, month) tuple.
 */

import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { useMemo, type JSX } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { MonthLockedBadge } from '@/components/monthly/MonthLockedBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useEntriesForMachineMonthQuery } from '@/hooks/useLogsheetMonthlyQueries';
import { useAuth } from '@/lib/auth/AuthContext';
import type { MonthlyEntryItem } from '@/types/logsheet-monthly';

function statusVariant(
  s: string,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (s === 'Rejected') return 'destructive';
  if (s === 'Approved') return 'default';
  if (s === 'Checked') return 'default';
  if (s === 'Submitted') return 'secondary';
  return 'outline'; // Draft
}

export function MachineMonthlyEntriesPage(): JSX.Element {
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const { machineId: machineIdParam } = useParams<{ machineId: string }>();
  const [searchParams] = useSearchParams();

  const machineId = Number(machineIdParam ?? '0');
  const month = searchParams.get('month') ?? '';

  const entriesQuery = useEntriesForMachineMonthQuery(machineId, month);

  const columns = useMemo<ColumnDef<MonthlyEntryItem>[]>(
    () => [
      {
        accessorKey: 'entryDate',
        header: 'Date',
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original.entryDate.slice(0, 10)}</span>
        ),
      },
      {
        accessorKey: 'type',
        header: 'Type',
        cell: ({ row }) => (
          <Badge variant={row.original.type === 'Idle' ? 'secondary' : 'default'}>
            {row.original.type}
          </Badge>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge variant={statusVariant(row.original.status)}>{row.original.status}</Badge>
        ),
      },
      {
        accessorKey: 'makerName',
        header: 'Maker',
        cell: ({ row }) => <span>{row.original.makerName}</span>,
      },
      {
        accessorKey: 'submittedAt',
        header: 'Submitted',
        cell: ({ row }) => (
          <span className="font-mono text-xs">
            {row.original.submittedAt !== null
              ? new Date(row.original.submittedAt).toLocaleDateString()
              : '—'}
          </span>
        ),
      },
      {
        accessorKey: 'approvedAt',
        header: 'Approved',
        cell: ({ row }) => (
          <span className="font-mono text-xs">
            {row.original.approvedAt !== null
              ? new Date(row.original.approvedAt).toLocaleDateString()
              : '—'}
          </span>
        ),
      },
      {
        id: 'locked',
        header: 'Month',
        cell: ({ row }) => (
          <MonthLockedBadge locked={row.original.status === 'Approved'} month={month} />
        ),
      },
    ],
    [month],
  );

  const data = entriesQuery.data ?? [];

  const table = useReactTable({
    data: data as MonthlyEntryItem[],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (!permissions.includes('logsheet.monthly.read')) {
    return <Navigate to="/dashboard" replace />;
  }

  const monthLabel = month !== '' ? month : '—';

  return (
    <div className="space-y-6" data-testid="machine-monthly-entries-page">
      <div className="flex items-center gap-4">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            void navigate(-1);
          }}
          data-testid="machine-monthly-back-button"
        >
          ← Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            <span data-testid="machine-monthly-machine-label">
              Machine #{String(machineId)}
            </span>
            {' — '}
            <span data-testid="machine-monthly-month-label">{monthLabel}</span>
          </h1>
          <p className="text-muted-foreground">All logsheet entries for this machine and month.</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Entries</CardTitle>
          <CardDescription>
            Individual logsheet entries for machine {String(machineId)}, {monthLabel}.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {entriesQuery.isLoading && (
            <p className="text-sm text-muted-foreground" data-testid="machine-monthly-loading">
              Loading entries…
            </p>
          )}
          {entriesQuery.isError && (
            <p className="text-sm text-destructive" data-testid="machine-monthly-error">
              Failed to load entries.
            </p>
          )}

          {entriesQuery.data && (
            <>
              {data.length === 0 ? (
                <div className="py-8 text-center" data-testid="machine-monthly-empty-state">
                  <p className="text-sm text-muted-foreground">
                    No entries found for machine {String(machineId)} in {monthLabel}.
                  </p>
                </div>
              ) : (
                <div data-testid="machine-monthly-entries-table">
                  {/* Mobile card list */}
                  <div className="md:hidden space-y-3">
                    {table.getRowModel().rows.map((row) => (
                      <button
                        key={row.id}
                        type="button"
                        onClick={() => {
                          void navigate(`/logsheets/view/${String(row.original.id)}`);
                        }}
                        data-testid={`machine-monthly-card-${String(row.original.id)}`}
                        className="w-full text-left rounded-lg border p-4 shadow-sm hover:bg-muted/40 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="font-mono text-sm font-medium">{row.original.entryDate.slice(0, 10)}</div>
                          <div className="flex gap-1 shrink-0">
                            <Badge variant={row.original.type === 'Idle' ? 'secondary' : 'default'}>{row.original.type}</Badge>
                            <Badge variant={statusVariant(row.original.status)}>{row.original.status}</Badge>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                          <div><span className="font-medium text-foreground">Maker: </span>{row.original.makerName}</div>
                          <div><span className="font-medium text-foreground">Submitted: </span><span className="font-mono">{row.original.submittedAt !== null ? new Date(row.original.submittedAt).toLocaleDateString() : '—'}</span></div>
                          <div><span className="font-medium text-foreground">Approved: </span><span className="font-mono">{row.original.approvedAt !== null ? new Date(row.original.approvedAt).toLocaleDateString() : '—'}</span></div>
                        </div>
                      </button>
                    ))}
                  </div>

                  {/* Desktop table */}
                  <div className="hidden md:block overflow-x-auto">
                    <Table>
                      <TableHeader>
                        {table.getHeaderGroups().map((hg) => (
                          <TableRow key={hg.id}>
                            {hg.headers.map((h) => (
                              <TableHead key={h.id}>
                                {h.isPlaceholder
                                  ? null
                                  : flexRender(h.column.columnDef.header, h.getContext())}
                              </TableHead>
                            ))}
                          </TableRow>
                        ))}
                      </TableHeader>
                      <TableBody>
                        {table.getRowModel().rows.map((row) => (
                          <TableRow
                            key={row.id}
                            data-testid={`machine-monthly-row-${String(row.original.id)}`}
                            onClick={() => {
                              void navigate(`/logsheets/view/${String(row.original.id)}`);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                void navigate(`/logsheets/view/${String(row.original.id)}`);
                              }
                            }}
                            tabIndex={0}
                            role="button"
                            aria-label={`Open detail for entry ${String(row.original.id)}`}
                            className="cursor-pointer hover:bg-muted/40"
                          >
                            {row.getVisibleCells().map((cell) => (
                              <TableCell key={cell.id}>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
